import { useRef, useState, useEffect } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from '@/lib/utils'
import { useReducedMotion } from '@/shared/hooks/useReducedMotion'
import { useMediaQuery } from '@/shared/hooks/useMediaQuery'

interface BottomSheetProps {
  open: boolean
  onClose: () => void
  children: React.ReactNode
  title?: string
}

export function BottomSheet({ open, onClose, children, title }: BottomSheetProps) {
  const prefersReduced = useReducedMotion()
  const isTablet = useMediaQuery('(min-width: 768px)')
  const contentRef = useRef<HTMLDivElement>(null)
  const [dragStart, setDragStart] = useState<number | null>(null)
  const [dragOffset, setDragOffset] = useState(0)

  // Reset drag state when sheet closes
  useEffect(() => {
    if (!open) {
      setDragStart(null)
      setDragOffset(0)
    }
  }, [open])

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (isTablet) return // No drag-to-dismiss on side panel
    setDragStart(e.clientY)
    setDragOffset(0)
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragStart === null) return
    const delta = e.clientY - dragStart
    if (delta > 0) {
      setDragOffset(delta)
    }
  }

  const handlePointerUp = () => {
    if (dragOffset > 80) {
      onClose()
    }
    setDragStart(null)
    setDragOffset(0)
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogPrimitive.Portal>
        {/* Backdrop */}
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-ink/50',
            !prefersReduced && 'motion-safe:data-[state=open]:animate-in motion-safe:data-[state=closed]:animate-out',
            !prefersReduced && 'motion-safe:data-[state=closed]:fade-out-0 motion-safe:data-[state=open]:fade-in-0',
          )}
        />
        {/* Sheet content */}
        <DialogPrimitive.Content
          ref={contentRef}
          style={
            !isTablet && dragOffset > 0
              ? { transform: `translateY(${dragOffset}px)` }
              : undefined
          }
          className={cn(
            'fixed z-50 bg-surface shadow-xl overflow-y-auto',
            // Mobile: bottom sheet
            !isTablet && [
              'bottom-0 left-0 right-0',
              'rounded-t-2xl border-t border-line',
              'max-h-[90vh]',
              !prefersReduced && 'motion-safe:transition-transform',
              !prefersReduced && 'motion-safe:data-[state=open]:animate-in motion-safe:data-[state=closed]:animate-out',
              !prefersReduced && 'motion-safe:data-[state=closed]:slide-out-to-bottom motion-safe:data-[state=open]:slide-in-from-bottom',
              !prefersReduced && 'motion-safe:duration-300',
            ],
            // Tablet+: right side panel
            isTablet && [
              'top-0 right-0 bottom-0',
              'w-[400px] max-w-full',
              'rounded-l-2xl border-l border-line',
              !prefersReduced && 'motion-safe:data-[state=open]:animate-in motion-safe:data-[state=closed]:animate-out',
              !prefersReduced && 'motion-safe:data-[state=closed]:slide-out-to-right motion-safe:data-[state=open]:slide-in-from-right',
              !prefersReduced && 'motion-safe:duration-300',
            ],
          )}
        >
          {/* Drag handle area — mobile only */}
          {!isTablet && (
            <div
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              className="flex flex-col items-center pt-3 pb-2 cursor-grab active:cursor-grabbing touch-none"
            >
              <div
                aria-hidden="true"
                className="w-10 h-1 rounded-full bg-faint"
              />
            </div>
          )}

          <DialogPrimitive.Title className="sr-only">
            {title ?? 'Sheet'}
          </DialogPrimitive.Title>

          <div className={cn('px-4', isTablet ? 'pt-4 pb-4' : 'pb-safe-bottom')}>
            {children}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

export { DialogPrimitive as BottomSheetPrimitive }
